import { dayRangeFor, monthRangeFor, parseRange, splitMonthIntoWeeks, weekRangesFor } from 'customer-flows/availability/ranges'
import type { EngineArgs, ResourceOptionQuantity } from './types'

// The exact fetch variables a key was built from, captured at read time. Carried alongside the
// key so a background fetch sends the variables the key represents — never variables re-read
// later, which could have drifted (different quantities/channel) and would write a mismatched
// response under this key.
export type FetchVariables = {
  activityId?: string
  dateRange: string
  resourceOptionQuantities: ResourceOptionQuantity[]
  channelId?: string
}

/**
 * What a key's fetch needs to know: the variables its request will carry. Which QUERY to run is no
 * longer part of this — dates and times live in separate caches, each wired to its own `fetch`, so
 * the family a key belongs to is the cache it is in rather than a tag it carries.
 *
 * This is the `dims` half of the query cache's `KeyRequest` — the cache treats it as an opaque
 * token it hands straight back to `fetch`.
 */
export type FetchDims = { variables: FetchVariables }

// A key plus the dimensions it was built from. Structurally a `KeyRequest<FetchDims>`, so it can be
// handed to either query cache directly. `weekRange` is domain-only (it backs `loadingWeekRanges`).
//
// Dates and times keys are the same shape and share one key-building function: two identical
// variable sets in different caches are two different questions, and separate maps keep them apart
// without a namespace prefix.
export type KeyedRange = { key: string; weekRange: string; dims: FetchDims }

/**
 * Drops zero-quantity entries, sorts by resourceOptionId, and stable-serializes the rest.
 * The dates fetch and the ticket-picker describe the same selection two different ways — a
 * sparse "only what's selected" shape and a zero-padded "all ticket types" shape — and dropping
 * zeros makes both collapse to one identity, so the same selection never splits into two keys.
 */
export function canonicalizeQuantities(quantities: ResourceOptionQuantity[]): string {
  const nonZero = (quantities ?? []).filter((q) => q.quantity > 0)
  const sorted = [...nonZero].sort((a, b) => (a.resourceOptionId ?? '').localeCompare(b.resourceOptionId ?? ''))
  return JSON.stringify(sorted.map((q) => [q.resourceOptionId ?? '', q.quantity]))
}

/**
 * An entry's identity: a pure function of every dimension its response varies on — the exact
 * `FetchVariables` the request will carry, plus the program scope (which scopes the query without
 * being one of its variables).
 *
 * Derived from the variables OBJECT rather than a hand-picked tuple on purpose: a hand-picked
 * tuple is exactly how `channelId` came to be a fetch variable that wasn't a key dimension, which
 * let two channels collide on one entry and serve each other's responses.
 */
function buildKey(variables: FetchVariables, programId: string | undefined): string {
  return [
    variables.activityId ?? '',
    programId ?? '',
    variables.channelId ?? '',
    variables.dateRange,
    canonicalizeQuantities(variables.resourceOptionQuantities)
  ].join('|')
}

function keyedRange(weekRange: string, args: EngineArgs, quantities: ResourceOptionQuantity[]): KeyedRange {
  const variables: FetchVariables = {
    activityId: args.activityId,
    dateRange: weekRange,
    resourceOptionQuantities: quantities,
    ...(args.channelId && { channelId: args.channelId })
  }

  return { key: buildKey(variables, args.programId), weekRange, dims: { variables } }
}

// ---------------------------------------------------------------------------------------------
// Dates keys
// ---------------------------------------------------------------------------------------------

// The current (DISPLAY) dates scope: the visible range's weeks, unioned with the selected date's
// month's weeks — so the selected date's week stays in scope even after the calendar navigates
// away from it.
export function currentWeekRanges(args: EngineArgs): string[] {
  return weekRangesFor(args.dateRange, args.selectedDate, args.minDate)
}

/**
 * Whether a fast month-scrub is in progress — derived, not passed in. The fetch scope is anchored
 * at the settled month while the display scope follows every hop, so the two ranges diverge exactly
 * while a scrub is running and are string-equal the rest of the time (both sides resolve through
 * the same `monthRangeFor(center, { overflowDays, maxDate })` on the store).
 *
 * Used to suppress the look-ahead preload, so it only ever targets the month the user settles on
 * rather than one they are scrubbing past.
 */
export function isScrubbing(args: EngineArgs): boolean {
  return !!args.anchoredDateRange && args.anchoredDateRange !== args.dateRange
}

// The quantities the FETCH scope keys off — the settled value, or the live one when none is set.
export function anchoredQuantitiesOf(args: EngineArgs): ResourceOptionQuantity[] {
  return args.anchoredQuantities ?? args.quantities
}

// The debounced (FETCH) dates scope: identical union logic, but read off `anchoredDateRange`
// instead of `dateRange` — which keys get fetched, not which get displayed.
export function anchoredWeekRanges(args: EngineArgs): string[] {
  return weekRangesFor(args.anchoredDateRange ?? args.dateRange, args.selectedDate, args.minDate)
}

// The look-ahead (PRELOAD) dates scope: the month AFTER the anchored month, month-aligned. Parsed
// from the anchored range's start so it re-aligns to the next month's 1st — which is exactly how
// `anchoredWeekRanges` aligns when the user actually navigates there, so the preloaded week keys
// are byte-identical to the keys navigation mints (dedupe / promotion). DATES only.
export function lookAheadWeekRanges(args: EngineArgs): string[] {
  const anchorRange = args.anchoredDateRange ?? args.dateRange
  const bounds = parseRange(anchorRange)
  if (!bounds) return splitMonthIntoWeeks(anchorRange, args.minDate)

  return splitMonthIntoWeeks(monthRangeFor(bounds[0].plus({ months: 1 })), args.minDate)
}

/**
 * One dates KeyedRange per week range, at the given quantities — each carrying the week range and
 * the exact fetch variables it was built from. One entry per week is fetched and tracked
 * independently.
 *
 * The three dates scopes (display / fetch / preload) and their all-type seed counterparts are all
 * this one mapper over a different week-range list; pass `[]` for `quantities` to mint the seed
 * (all-type) family for the same weeks. The dates seed is the domain's ONLY optimistic family —
 * times has no seed counterpart (see below).
 */
export function datesKeysFor(
  weekRanges: string[],
  args: EngineArgs,
  quantities: ResourceOptionQuantity[]
): KeyedRange[] {
  return weekRanges.map((weekRange) => keyedRange(weekRange, args, quantities))
}

// ---------------------------------------------------------------------------------------------
// Times keys
// ---------------------------------------------------------------------------------------------

// The ONE times family: quantity-specific, for the currently selected date, scoped to that single
// day — the authoritative answer for "what times are available for this date, at these ticket
// quantities". Only exists once a date is selected; there is no day to scope the fetch to before
// then.
//
// There is no all-type ("seed") times counterpart to the dates seed. There used to be — a
// whole-visible-range, all-zero-quantity background fetch that backed an optimistic times fallback
// — but the BE answers it with zero results unconditionally, so it never contributed a slot to any
// fold and only cost a request per month. The optimistic answer now comes exclusively from the
// DATES seed, whose response carries nested `availabilityTimes` for every day of the week it
// covers (`queryWithTimes`); that is what the pickers lean on while this key's fetch is in flight.
export function selectedDateTimesKey(
  args: EngineArgs,
  quantities: ResourceOptionQuantity[] = args.quantities
): KeyedRange | undefined {
  if (!args.selectedDate) return undefined
  return keyedRange(dayRangeFor(args.selectedDate), args, quantities)
}

// The times keys for the current args — at most one, and none until a date is selected.
//
// Scoped to the SELECTED DATE, not the visible range, so a month-scrub does not re-key it. Ticket
// quantities are part of the key though, so they do — hence the anchored counterpart below.
export function currentTimesKeys(args: EngineArgs): KeyedRange[] {
  const selectedDate = selectedDateTimesKey(args)
  return selectedDate ? [selectedDate] : []
}

// The same key off the debounced quantities — what the FETCH scope targets.
export function anchoredTimesKeys(args: EngineArgs): KeyedRange[] {
  const selectedDate = selectedDateTimesKey(args, anchoredQuantitiesOf(args))
  return selectedDate ? [selectedDate] : []
}
